const normalizePath = (path: string): string => {
  // Trim whitespace - backslashes are rejected during validation
  let normalized = path.trim();

  // Remove trailing slashes except for root
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }

  // Remove leading ./ to treat "./infra" and "infra" as the same
  if (normalized.startsWith("./")) {
    normalized = normalized.slice(2);
  }

  return normalized;
};

const isValidGitSparseCheckoutPath = (path: string): boolean => {
  // Check for backslashes - not allowed in git sparse checkout paths
  if (path.includes("\\")) return false;

  // Check for invalid characters in git paths
  // eslint-disable-next-line no-control-regex
  const invalidChars = /[<>:"|?*\u0000-\u001f\u007f]/;
  if (invalidChars.test(path)) return false;

  // Check for reserved names (Windows compatibility)
  const pathParts = path.split("/");

  // Check for parent directory references (..) and invalid dot patterns
  // Allow paths starting with ./ (like "./as") but reject .. and standalone .
  if (pathParts.some((part) => part === ".." || /^\.{3,}$/.test(part))) return false;

  // Reject standalone "." unless it's the first part of a ./path pattern
  if (pathParts.some((part, index) => part === "." && (index !== 0 || pathParts.length === 1)))
    return false;

  // Check for paths ending with spaces or dots (Windows compatibility)
  // But allow the special case of "." as the first part (for ./paths)
  if (
    pathParts.some((part, index) => {
      if (part === "." && index === 0) return false; // Allow "." as first part
      return /[ .]$/.test(part);
    })
  )
    return false;

  // Check for absolute paths (sparse checkout should use relative paths)
  if (path.startsWith("/")) return false;

  return true;
};

export const createGitSparseCheckoutPathValidator = (
  getValues: () => { gitSparseCheckoutPaths?: { value: string }[] },
  index: number
) => {
  return (currentValue: string) => {
    if (!currentValue.trim()) return undefined;

    const normalizedCurrent = normalizePath(currentValue);

    // Validate git sparse checkout path format
    if (!isValidGitSparseCheckoutPath(normalizedCurrent)) {
      return "Invalid path format for git sparse checkout";
    }

    // Check for uniqueness
    const { gitSparseCheckoutPaths } = getValues();
    if (gitSparseCheckoutPaths) {
      const duplicateIndex = gitSparseCheckoutPaths.findIndex(
        ({ value }, idx) => idx !== index && normalizePath(value) === normalizedCurrent
      );

      if (duplicateIndex !== -1) {
        return "Path has to be unique";
      }
    }

    return undefined;
  };
};
